import React, { useRef, useCallback } from "react";

// Hardware-style rotary knob driven by vertical drag.
export default function Knob({
  value,
  min = 0,
  max = 1,
  onChange,
  label,
  size = 44,
  color = "#00e5ff",
  format,
  testId,
  hint,
}) {
  const ref = useRef(null);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startVal = useRef(0);

  const range = max - min;
  const norm = (value - min) / range;
  const angle = -135 + norm * 270;

  const onDown = useCallback(
    (e) => {
      dragging.current = true;
      startY.current = e.clientY;
      startVal.current = value;
      const onMove = (ev) => {
        if (!dragging.current) return;
        const dy = startY.current - ev.clientY;
        let next = startVal.current + (dy / 150) * range;
        next = Math.max(min, Math.min(max, next));
        onChange(next);
      };
      const onUp = () => {
        dragging.current = false;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      e.preventDefault();
    },
    [value, min, max, range, onChange]
  );

  const onWheel = useCallback(
    (e) => {
      e.preventDefault();
      let next = value - (e.deltaY / 100) * range * 0.05;
      next = Math.max(min, Math.min(max, next));
      onChange(next);
    },
    [value, min, max, range, onChange]
  );

  const display = format ? format(value) : Math.round(norm * 100);

  return (
    <div className="flex flex-col items-center gap-1 select-none" data-testid={testId}>
      <div
        ref={ref}
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-label={label}
        onPointerDown={onDown}
        onWheel={onWheel}
        title={hint || label}
        className="relative rounded-full cursor-grab active:cursor-grabbing"
        style={{
          width: size,
          height: size,
          touchAction: "none",
          background: "radial-gradient(circle at 50% 35%, #2e2e2e, #161616 70%)",
          border: "1px solid #000",
          boxShadow: "0 4px 6px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255,255,255,0.05)",
        }}
      >
        <div
          className="absolute inset-0 rounded-full"
          style={{ transform: `rotate(${angle}deg)` }}
        >
          <div
            className="absolute left-1/2 -translate-x-1/2 rounded-full"
            style={{
              top: 4,
              width: 3,
              height: size * 0.28,
              background: color,
              boxShadow: `0 0 6px ${color}`,
            }}
          />
        </div>
      </div>
      {label && <span className="hw-label leading-none">{label}</span>}
      <span className="font-mono-data text-[10px]" style={{ color }}>
        {display}
      </span>
    </div>
  );
}
